import { computed } from 'mobx'
import { TermsOfUseDocument } from '@/features/account/terms/TermsOfUseDocument'
import { type Id } from '@/infra/id'
import { type TermsOfUseStore } from '@/features/account/terms/TermsOfUseStore'
import { type SnackbarRetryWrapperFactory } from '@/features/shell'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type ActivationLifecycle } from '@/infra/activation'
import { tap } from 'rxjs/operators'

/**
 * Manages the list of terms acceptances.
 */
export class TermsAcceptancesListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  termsAcceptancesScroller = new InfiniteScrollProvider({
    limit: 10,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'Unable to load agreements',
        signal,
      )

      return withRetry(() =>
        this._termsOfUseStore.listTermsOfUseAcceptances(
          this.workspace.id,
          paging,
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _getCurrentWorkspace: () => Workspace,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _termsOfUseStore: TermsOfUseStore,
  ) {
    // Reload the terms when accepted.
    this._lifecycle.manage(
      this._termsOfUseStore.acceptance$.pipe(tap(() => this.fetchTerms())),
    )
  }

  @computed
  get workspace() {
    return this._getCurrentWorkspace()
  }

  getTermsOfUse(termsOfUseId: Id) {
    return (
      this._termsOfUseStore.termsOfUseDocuments.get(termsOfUseId) ??
      TermsOfUseDocument.none
    )
  }

  activate() {
    this._lifecycle.activate()
    this.fetchTerms()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the terms.
   *
   * @private
   */
  private fetchTerms() {
    void this.termsAcceptancesScroller.fetch({
      signal: this._lifecycle.signal,
    })
  }
}
